import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/api';
import { env } from '@/lib/env';

const ON_DASHBOARD_UPDATE = /* GraphQL */ `
  subscription OnDashboardUpdate($householdId: ID!) {
    onDashboardUpdate(householdId: $householdId) {
      householdId
      payload
      emittedAt
    }
  }
`;

const ON_CAPTURE_READY = /* GraphQL */ `
  subscription OnCaptureReady($householdId: ID!) {
    onCaptureReady(householdId: $householdId) {
      householdId
      capture
      emittedAt
    }
  }
`;

interface SubscribeOpts {
  onDashboardUpdate?: (payload: unknown) => void;
  onCaptureReady?: (capture: unknown) => void;
}

interface AmplifyObservable {
  subscribe: (handlers: {
    next: (value: unknown) => void;
    error: (err: unknown) => void;
  }) => { unsubscribe: () => void };
}

// Amplify v6's `client.graphql` types blow up TS inference for subscription
// queries. We narrow to a minimal Observable shape — runtime is unchanged.
type GraphqlClient = {
  graphql: (req: { query: string; variables: Record<string, unknown> }) => AmplifyObservable;
};

export function useDashboardSubscription(opts: SubscribeOpts = {}) {
  const qc = useQueryClient();

  useEffect(() => {
    if (env.mock || !env.appsyncUrl) return;

    const client = generateClient() as unknown as GraphqlClient;
    const variables = { householdId: env.householdId };

    const dashSub = client
      .graphql({ query: ON_DASHBOARD_UPDATE, variables })
      .subscribe({
        next: (raw) => {
          const evt = (raw as { data?: { onDashboardUpdate?: unknown } }).data?.onDashboardUpdate;
          opts.onDashboardUpdate?.(evt);
          qc.invalidateQueries({ queryKey: ['dashboard'] });
        },
        error: (e) => console.warn('onDashboardUpdate error', e),
      });

    const capSub = client
      .graphql({ query: ON_CAPTURE_READY, variables })
      .subscribe({
        next: (raw) => {
          const evt = (raw as { data?: { onCaptureReady?: unknown } }).data?.onCaptureReady;
          opts.onCaptureReady?.(evt);
          qc.invalidateQueries({ queryKey: ['dashboard'] });
          qc.invalidateQueries({ queryKey: ['gallery'] });
        },
        error: (e) => console.warn('onCaptureReady error', e),
      });

    return () => {
      dashSub.unsubscribe();
      capSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);
}
